import React, { useEffect, useState } from "react";
import { BarChart } from "../../components/charts/Bar";
import DataTable from "react-data-table-component";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import { customTableStyles } from "../../assets/CustomTableStyles";
import { get } from "../../services/api";

// Static data for student yearly info
const dummyStudentYearlyInfo = [
  {
    year: 2020,
    attendance: 90,
    assignments: 85,
    testResults: 80,
    feePaid: 100,
    grade: 85,
  },
  {
    year: 2021,
    attendance: 92,
    assignments: 88,
    testResults: 85,
    feePaid: 100,
    grade: 90,
  },
  {
    year: 2022,
    attendance: 94,
    assignments: 90,
    testResults: 88,
    feePaid: 100,
    grade: 88,
  },
  {
    year: 2023,
    attendance: 95,
    assignments: 92,
    testResults: 90,
    feePaid: 100,
    grade: 92,
  },
  {
    year: 2024,
    attendance: 96,
    assignments: 95,
    testResults: 92,
    feePaid: 100,
    grade: 95,
  },
];

// Static data for events
const dummyEvents = [
  { date: "2023-05-15", title: "Science Fair" },
  { date: "2023-05-20", title: "Sports Day" },
];

// Static data for academic calendar events
const dummyAcademicEvents = [
  { date: "2023-05-01", title: "Semester Start" },
  { date: "2023-06-15", title: "Midterm Exams" },
  { date: "2023-08-01", title: "Semester End" },
];

// Static data for notices
const dummyNotices = [
  {
    title: "School Holiday",
    content: "School closed on Monday",
    date: "2023-05-01",
  },
  {
    title: "Parent-Teacher Meeting",
    content: "Scheduled for next week",
    date: "2023-05-10",
  },
];

// Main component for Parent & Student Dashboard
function ParentStudentDashboard() {
  // State for static data
  const [studentYearlyInfo] = useState(dummyStudentYearlyInfo);
  const [events] = useState(dummyEvents);
  const [academicEvents] = useState(dummyAcademicEvents);
  const [notices] = useState(dummyNotices);
  const [selectedYear, setSelectedYear] = useState(2024); // Default to latest year

  // State for dynamic student data
  const [studentsData, setStudentsData] = useState([]);

  // Get token and roleId from localStorage
  const token = localStorage.getItem("token");
  const roleId = localStorage.getItem("userId");

  // Get current year info based on selected year
  const currentYearInfo =
    studentYearlyInfo.find((info) => info.year === selectedYear) || {};

  // Chart data for student grades
  const chartData = {
    labels: studentYearlyInfo.map((item) => item.year.toString()),
    datasets: [
      {
        label: "Student Grade (%)",
        data: studentYearlyInfo.map((item) => item.grade),
        backgroundColor: "rgba(75, 192, 192, 0.6)",
        borderColor: "rgba(75, 192, 192, 1)",
        borderWidth: 1,
        borderRadius: 5,
      },
    ],
  };

  // Chart options
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    aspectRatio: 2,
    plugins: {
      legend: { position: "top", labels: { boxWidth: 20, boxHeight: 10 } },
      title: {
        display: true,
        text: "Student Academic Performance Over Years",
        font: { size: 18 },
      },
      tooltip: { callbacks: { label: (context) => `${context.raw}%` } },
    },
    scales: {
      x: {
        title: { display: true, text: "Academic Year", font: { size: 14 } },
      },
      y: {
        title: { display: true, text: "Grade (%)", font: { size: 14 } },
        ticks: { callback: (value) => `${value}%` },
        min: 0,
        max: 100,
      },
    },
  };

  // Fetch student data by parent ID
  useEffect(() => {
    const getStudentsByParentId = async () => {
      try {
        const studentResponse = await get(
          `Student/GetStudentByParentIdAsync?userId=${roleId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setStudentsData(studentResponse);
      } catch (error) {
        console.error("Error fetching student data:", error);
      }
    };
    getStudentsByParentId();
  }, [roleId, token]);

  // Convert events to FullCalendar format
  const calendarEvents = events.map((event) => ({
    title: event.title,
    start: event.date,
  }));

  // Convert academic events to FullCalendar format
  const academicCalendarEvents = academicEvents.map((event) => ({
    title: event.title,
    start: event.date,
  }));

  // Handle event click to show details
  const handleEventClick = (clickInfo) => {
    const { event } = clickInfo;
    alert(
      `Event: ${event.title}\nDate: ${event.start.toISOString().split("T")[0]}`
    );
  };

  return (
    <div className="">
      {/* Dashboard Header with Dropdowns */}
      <div className="mb-3 d-flex align-items-end">
        <div>
          <h3 className="">Parent & Student Dashboard</h3>
          <p className="text-body-tertiary mb-0">
            Monitor your student's progress and updates here.
          </p>
        </div>
        <div className="ms-auto d-flex">
          <div style={{ minWidth: "150px" }}>
            <label className="form-label">Student</label>
            <select className="form-select">
              {studentsData.map((student) => (
                <option key={student.id} value={student.id}>
                  {student.firstName + " " + student.lastName}
                </option>
              ))}
            </select>
          </div>
          <div className="ms-3" style={{ minWidth: "150px" }}>
            <label className="form-label">Year</label>
            <select
              className="form-select"
              value={selectedYear}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
            >
              {studentYearlyInfo.map((info) => (
                <option key={info.year} value={info.year}>
                  {info.year}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Four Info Boxes */}
      <div className="row g-3 mb-3">
        <div className="col-md-6 col-xl-3">
          <div
            className="infographic-card card"
            style={{ backgroundColor: "#28a745", color: "white" }}
          >
            <div className="card-body px-3 py-2">
              <i className="fas fa-user-check fa-lg mb-1"></i>
              <h5 className="card-title mb-1">Attendance</h5>
              <h4>{currentYearInfo.attendance || "N/A"}%</h4>
            </div>
          </div>
        </div>
        <div className="col-md-6 col-xl-3">
          <div
            className="infographic-card card"
            style={{ backgroundColor: "#007bff", color: "white" }}
          >
            <div className="card-body px-3 py-2">
              <i className="fas fa-tasks fa-lg mb-1"></i>
              <h5 className="card-title mb-1">On-time Assignments</h5>
              <h4>{currentYearInfo.assignments || "N/A"}%</h4>
            </div>
          </div>
        </div>
        <div className="col-md-6 col-xl-3">
          <div
            className="infographic-card card"
            style={{ backgroundColor: "#ffc107", color: "black" }}
          >
            <div className="card-body px-3 py-2">
              <i className="fas fa-chart-line fa-lg mb-1"></i>
              <h5 className="card-title mb-1">Test Results</h5>
              <h4>{currentYearInfo.testResults || "N/A"}%</h4>
            </div>
          </div>
        </div>
        <div className="col-md-6 col-xl-3">
          <div
            className="infographic-card card"
            style={{ backgroundColor: "#17a2b8", color: "white" }}
          >
            <div className="card-body px-3 py-2">
              <i className="fas fa-money-check-alt fa-lg mb-1"></i>
              <h5 className="card-title mb-1">Fee Paid</h5>
              <h4>{currentYearInfo.feePaid || "N/A"}%</h4>
            </div>
          </div>
        </div>
      </div>

      {/* Bar Chart and Academic Calendar */}
      <div className="row g-3 mb-3">
        <div className="col-lg-8 col-md-12">
          <div className="card">
            <div className="card-body px-3 py-4">
              <h5 className="mb-3">Yearly Academic Performance</h5>
              <div style={{ minHeight: "400px" }}>
                <BarChart
                  chartType="grades"
                  chartData={chartData}
                  options={chartOptions}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="col-lg-4 col-md-12">
          <div className="card mb-3">
            <div className="card-body px-3 py-4">
              <h5 className="mb-3">Academic Calendar</h5>
              <FullCalendar
                plugins={[dayGridPlugin, interactionPlugin]}
                initialView="dayGridMonth"
                events={academicCalendarEvents}
                eventClick={handleEventClick}
                headerToolbar={{
                  left: "prev,next",
                  center: "title",
                  right: "dayGridMonth,dayGridWeek,dayGridDay",
                }}
                height="400px"
              />
            </div>
          </div>
          <div className="card">
            <div className="card-body px-3 py-4">
              <h5 className="mb-3">Event Calendar</h5>
              <FullCalendar
                plugins={[dayGridPlugin, interactionPlugin]}
                initialView="dayGridMonth"
                events={calendarEvents}
                eventClick={handleEventClick}
                headerToolbar={{
                  left: "prev,next",
                  center: "title",
                  right: "dayGridMonth,dayGridWeek,dayGridDay",
                }}
                height="400px"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Notice Board */}
      <div className="row g-3">
        <div className="col-12">
          <div className="card">
            <div className="card-body px-3 py-4" style={{ minHeight: "200px" }}>
              <h5 className="mb-3">Notice Board</h5>
              <ul className="list-unstyled">
                {notices.map((notice, index) => (
                  <li key={index} className="mb-2 p-2 bg-light rounded">
                    <strong>{notice.title}</strong> - {notice.content} (
                    {notice.date})
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ParentStudentDashboard;
